import { IGroupPaymentInfo, IOrder } from '@/interfaces/IOrder';

interface IPendingGroup {
  group: IGroupPaymentInfo;
  members: Map<number, IOrder>;
  timer: ReturnType<typeof setTimeout> | null;
  flushed: boolean;
}

/**
 * Collects the per-order `v2:orderUpdate` deliveries of a multi-order payment
 * (transient `groupPayment` WS field) so the auto-print evaluator can print
 * ONE combined ticket instead of one per order. Purely a timer-based
 * collector: all printing decisions live in the `onFlush` callback.
 */
export function createGroupPaymentAggregator(opts: {
  debounceMs: number;
  onFlush: (orders: IOrder[], group: IGroupPaymentInfo) => Promise<void>;
  /** Called by clear() when it drops groups that never flushed. */
  onDroppedPending: () => void;
}) {
  const groups = new Map<number, IPendingGroup>();
  let inFlightFlushes = 0;

  function flush(entry: IPendingGroup): void {
    if (entry.flushed) return;
    entry.flushed = true;

    if (entry.timer !== null) clearTimeout(entry.timer);
    // Keep the flushed entry around for one more debounce window so late
    // arrivals are recognized (add() returns false → caller prints per-order
    // instead of silently waiting for a catch-up pass).
    entry.timer = setTimeout(() => {
      if (groups.get(entry.group.paymentId) === entry) {
        groups.delete(entry.group.paymentId);
      }
    }, opts.debounceMs);

    inFlightFlushes++;
    opts
      .onFlush(Array.from(entry.members.values()), entry.group)
      .catch((e) => {
        // onFlush handles its own failures; this only guards against an
        // unhandled rejection escaping the timer context.
        console.error('[GroupPaymentPrint] Unexpected flush error', e);
      })
      .finally(() => {
        inFlightFlushes--;
      });
  }

  return {
    /**
     * Registers a marker-bearing delivery of the payment group. Flushes
     * immediately once every expected member (`fundedOrderIds`, or `orderIds`
     * for older API payloads) arrived; otherwise `debounceMs` after the LAST
     * new member (trailing debounce) as a safety net for lost deliveries. The
     * server emits the group's orders one by one after commit, so a fixed
     * window from the first add could expire mid-burst on large groups. A
     * duplicate delivery of a member already present refreshes the stored
     * order but never re-arms the window.
     *
     * @returns false when the group already flushed (late arrival): the
     * caller must fall back to the normal per-order print.
     */
    add(order: IOrder, group: IGroupPaymentInfo): boolean {
      const existing = groups.get(group.paymentId);
      const entry: IPendingGroup = existing ?? {
        group,
        members: new Map(),
        timer: null,
        flushed: false,
      };
      if (!existing) groups.set(group.paymentId, entry);

      if (entry.flushed) return false;

      const isNewMember = !entry.members.has(order.id);
      entry.members.set(order.id, order);
      if (!isNewMember) return true;

      // Expected deliveries are the funded members (each one carries the
      // marker). Payloads from an older API lack the field (an empty list is
      // treated the same, defensively: [].every() would flush on the first
      // add): fall back to the full selection, whose unfunded members never
      // arrive, so those groups keep flushing through the trailing debounce.
      const expectedIds = entry.group.fundedOrderIds?.length
        ? entry.group.fundedOrderIds
        : entry.group.orderIds;
      if (expectedIds.every((id) => entry.members.has(id))) {
        flush(entry);
        return true;
      }

      if (entry.timer !== null) clearTimeout(entry.timer);
      entry.timer = setTimeout(() => flush(entry), opts.debounceMs);
      return true;
    },
    /** True while any group awaits its flush or an onFlush is in flight. */
    hasPending(): boolean {
      if (inFlightFlushes > 0) return true;
      for (const entry of groups.values()) {
        if (!entry.flushed) return true;
      }
      return false;
    },
    /**
     * Cancels timers and drops all groups without flushing (branch switch /
     * teardown). Dropped unflushed members' items are still unmarked, so the
     * onDroppedPending hook lets the caller freeze the catch-up sync point
     * and recover them per-order instead of losing them silently.
     */
    clear(): void {
      let droppedPending = false;

      for (const entry of groups.values()) {
        if (entry.timer !== null) clearTimeout(entry.timer);
        if (!entry.flushed) droppedPending = true;
      }
      groups.clear();

      if (droppedPending) opts.onDroppedPending();
    },
  };
}
